import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import * as React from 'react';

import { UnstyledButton } from './unstyled-button.component';
import styles from './button.module.scss';

const buttonVariants = cva(styles.btn, {
  variants: {
    variant: {
      contained: styles['btn-variant-contained'],
      outlined: styles['btn-variant-outlined'],
      text: styles['btn-variant-text'],
    },
    size: {
      xs: styles['btn-size-xs'],
      sm: styles['btn-size-sm'],
      md: styles['btn-size-md'],
      lg: styles['btn-size-lg'],
    },
    palette: {
      primary: styles['btn-palette-primary'],
      ghost: styles['btn-palette-ghost'],
    },
  },
});

type ButtonProps = {
  asChild?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  Required<VariantProps<typeof buttonVariants>>;

const Button = Object.assign(
  React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, palette, asChild = false, ...props }, ref) => {
      const Comp = asChild ? Slot : UnstyledButton;

      return (
        <Comp
          className={clsx(
            buttonVariants({ variant, size, palette }),
            className,
            {
              [UnstyledButton.className]: asChild,
            }
          )}
          ref={ref}
          {...props}
        />
      );
    }
  ),
  {
    classnames: {
      contained: {
        hover: styles.btnVariantContainedHover,
      },
      outlined: {
        hover: styles.btnVariantOutlinedHover,
      },
    },
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
export type { ButtonProps };
